<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchAccountSingle, fetchClients, updateAccount, VerifyUsers } from '@/api/accountsClient'
import { useAccountStore } from '@/stores/accountStore'
import { useCreateAccountStore } from '@/stores/createAccountStore'
import ProgressLoader from '@/components/ProgressLoader.vue'
import logoPlaceholder from '../../logo-placeholder.png'
import plusIcon from '../../plus-icon.jpg'
import { timeZones as region } from '@/views/timezoneData.js'
import {
  getRequiredBillingErrors,
  requiredTrimmedRule,
  trimRequiredBillingFields,
} from '@/utils/billingValidation'

const router = useRouter()
const accountStore = useAccountStore()
const route = useRoute()
const store = useCreateAccountStore()
const loading = ref(false)
const isLoading = ref(true)
const clients = ref([])
const fileInput = ref<HTMLInputElement | null>(null)
const base64Image = ref('') // Store Base64 image
const imageTouched = ref(false)
const formRef = ref()
const billingRules = {
  address_1: requiredTrimmedRule('Address 1'),
  city: requiredTrimmedRule('City'),
  state: requiredTrimmedRule('State'),
  zip: requiredTrimmedRule('Zip'),
}

const message = ref('')
const snackbar = ref(false)

// Store original form data to detect changes
const originalFormData = ref<any>(null)

const timeZones = ref(region)
const timezoneSearchQuery = ref('')

// Get the account name from the route parameters
const accountName = route.params.name as string
const isParentAccount = computed(() => {
  return (
    formFields.value.accountName ===
    formFields.value.parentAccount?.toLowerCase().replace(/[-\s]/g, '_')
  )
})

const formFields = ref({
  isParent: false,
  parentAccount: '',
  image: '',
})
const a5AccountInput = ref('')

// Fetch clients for the dropdown
const fetchClientList = async () => {
  try {
    const res = await fetchClients()
    clients.value = res.map((client) => ({ title: client }))
  } catch (error) {
    console.error('Failed to load clients:', error)
  }
}

const fetchAccountDetails = async () => {
  isLoading.value = true
  try {
    const data = await fetchAccountSingle(accountName)
    formFields.value = {
      ...data,
      isParent: false,
    }

    if (data.a5Account) {
      a5AccountInput.value = Array.isArray(data.a5Account)
        ? data.a5Account.join(',')
        : String(data.a5Account)
    }
    await fetchClientList()

    // Store original data for change detection
    originalFormData.value = JSON.parse(JSON.stringify(formFields.value))

    console.log('Fetched Data:', formFields)
  } catch (error) {
    console.error('Failed to fetch account data:', error)
  } finally {
    isLoading.value = false
  }
}

const submitForm = async () => {
  console.log(formFields.value)
  const billing = (formFields.value as unknown as { billing?: Record<string, unknown> }).billing
  const billingErrors = getRequiredBillingErrors(billing)
  const { valid } = await formRef.value.validate()
  if (!valid || billingErrors.length) {
    message.value = billingErrors[0] || 'Please fill all required fields.'
    snackbar.value = true
    return
  }

  loading.value = true
  try {
    let payload = preparePayload(formFields.value)
    console.log('Updated Payload:', payload)

    const response = await updateAccount(payload)
    console.log('Update Response:', response)
    message.value = 'User Updated Successfully'
    snackbar.value = true

    setTimeout(() => {
      // Clear accounts cache so list refetches next time
      try {
        accountStore.setAccountsCache([])
      } catch {}
      router.push(`/account/${accountName}`)
    }, 1200)
  } catch (error) {
    console.error('Failed to update account:', error)
    alert('Failed to update account.')
    loading.value = false
  } finally {
    loading.value = false
  }
}

const preparePayload = (payload: any) => {
  const adjustedPayload = {
    ...payload,
    billing: trimRequiredBillingFields(payload.billing),
  }
  delete adjustedPayload.step
  if (!imageTouched.value) {
    adjustedPayload.image = ''
  }

  adjustedPayload.accountName = adjustedPayload.accountName.toLowerCase().replace(/[-\s]/g, '_')

  if (typeof adjustedPayload.a5Account === 'string') {
    adjustedPayload.a5Account = adjustedPayload.a5Account
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num !== '')
      .map(Number)
  }
  return adjustedPayload
}

onMounted(() => {
  VerifyUsers()
  fetchAccountDetails()
})

watch(a5AccountInput, (newValue) => {
  const filtered = newValue.replace(/[^0-9,]/g, '')

  formFields.value.a5Account = filtered
    .split(',')
    .map((num) => num.trim())
    .filter((num) => num !== '')
    .map(Number)

  console.log('Updated a5Account array:', typeof formFields.value.a5Account)

  a5AccountInput.value = filtered
})

const blockInvalidChars = (event: KeyboardEvent) => {
  const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', ',']

  if (!/[\d,]/.test(event.key) && !allowedKeys.includes(event.key)) {
    event.preventDefault()
  }
}

const updateA5Account = (value: string) => {
  a5AccountInput.value = value.replace(/[^0-9,]/g, '')
  formFields.value.a5Account = a5AccountInput.value
}

const handleFileChange = (file: File | File[] | null) => {
  const MAX_SIZE_MB = 2
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

  if (file instanceof File) {
    if (file.size > MAX_SIZE_BYTES) {
      alert(`File size exceeds ${MAX_SIZE_MB}MB. Please select a smaller file.`)
      return
    }
    imageTouched.value = true
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      formFields.value.image = reader.result as string
    }
  } else if (Array.isArray(file) && file.length > 0) {
    imageTouched.value = true
    const reader = new FileReader()
    reader.readAsDataURL(file[0])
    reader.onload = () => {
      formFields.value.image = reader.result as string
    }
  } else {
    imageTouched.value = false
    formFields.value.image = ''
  }
}
const tempParent = ref('')
const clearClientOrParent = () => {
  if (formFields.value.isParent) {
    tempParent.value = formFields.value.parentAccount
    formFields.value.parentAccount = ''
  } else {
    formFields.value.parentAccount = tempParent.value
  }
}
const requiredRule = (v) => !!v || 'This field is required.'

// URL validation rule (must start with http:// or https://)
const urlRule = (v) => {
  if (!v) return true // let `requiredRule` catch empty
  // basic URL regex — tweak as needed
  const pattern = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i
  return pattern.test(v) || 'Please enter a valid URL (must start with http:// or https://)'
}

const filteredTimezones = computed(() => {
  if (!timezoneSearchQuery.value) return timeZones.value
  return timeZones.value.filter((tz) =>
    tz.toLowerCase().includes(timezoneSearchQuery.value.toLowerCase())
  )
})

// Check if form has changes
const hasChanges = computed(() => {
  if (!originalFormData.value) return false
  return JSON.stringify(formFields.value) !== JSON.stringify(originalFormData.value)
})

const getFormattedTimezones = () => {
  try {
    const allTimezones = Intl.supportedValuesOf('timeZone')
    timeZones.value = allTimezones
  } catch (e) {
    console.error("Your environment doesn't support Intl.supportedValuesOf()")
    // Fallback to a basic list
  }
}
onMounted(() => {
  // getFormattedTimezones()
})
</script>

<template>
  <template v-if="isLoading">
    <div class="height-loader">
      <progress-loader></progress-loader>
    </div>
  </template>
  <v-container v-else>
    <!-- Subheader: Back + Title (match Account Information header style) -->
    <v-row>
      <v-col cols="12" class="subheader-sticky">
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-arrow-left"
            class="text-grey-darken-2"
            @click="router.back()"
          >
            Back
          </v-btn>
          <h2 class="text-h5 font-weight-medium" style="margin: 0">Edit Account Info</h2>
        </div>
      </v-col>
    </v-row>
    <v-card flat>
      <!-- Primary Fields for Editing -->
      <v-form ref="formRef">
        <v-container>
          <v-row></v-row>
          <v-row cols="12" md="12" class="mb-5">
            <v-select
              v-model="formFields.timezone"
              :items="filteredTimezones"
              item-title="label"
              item-value="value"
              label="Timezone*"
              data-testid="timezone-select"
              persistent-hint
              single-line
              clearable
              filterable
              :no-data-text="'No matching timezone found'"
              :hint="formFields.timezone || 'Select timezone'"
            >
              <template v-slot:prepend-item>
                <v-text-field
                  v-model="timezoneSearchQuery"
                  label="Search Timezones"
                  clearable
                  dense
                  autofocus
                ></v-text-field>
              </template>
            </v-select>
          </v-row>

          <!-- Expansion Panels for Additional Details -->
          <v-row>
            <v-expansion-panels>
              <!-- Primary Contact Section -->
              <v-expansion-panel>
                <v-expansion-panel-title>Primary Contact</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="formFields.primaryContact.name"
                      label="Name"
                      required
                    ></v-text-field>
                    <v-text-field
                      v-model="formFields.primaryContact.email"
                      label="Email"
                      required
                    ></v-text-field>
                    <v-text-field
                      v-model="formFields.primaryContact.phone"
                      label="Phone"
                      required
                    ></v-text-field>
                  </v-col>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- Billing Info Section -->
              <v-expansion-panel>
                <v-expansion-panel-title>Billing Info</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="formFields.billing.name"
                      label="Name"
                      required
                    ></v-text-field>
                    <v-text-field
                      v-model.trim="formFields.billing.address_1"
                      label="Address 1*"
                      :rules="[billingRules.address_1]"
                      required
                    ></v-text-field>
                    <v-text-field
                      v-model="formFields.billing.address_2"
                      label="Address2"
                    ></v-text-field>
                    <v-text-field
                      v-model.trim="formFields.billing.city"
                      label="City*"
                      :rules="[billingRules.city]"
                      required
                    ></v-text-field>
                    <v-text-field
                      v-model.trim="formFields.billing.state"
                      label="State*"
                      :rules="[billingRules.state]"
                      required
                    ></v-text-field>
                    <v-text-field
                      v-model.trim="formFields.billing.zip"
                      label="Zip*"
                      :rules="[billingRules.zip]"
                      required
                    ></v-text-field>
                  </v-col>
                </v-expansion-panel-text>
              </v-expansion-panel>
              <!-- Notes Section -->
              <v-expansion-panel>
                <v-expansion-panel-title>Notes</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-col cols="12" md="12">
                    <v-textarea
                      label="Notes"
                      data-testid="notes-textarea"
                      v-model="formFields.notes"
                      aria-required="true"
                    ></v-textarea>
                  </v-col>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-row>
        </v-container>
      </v-form>
    </v-card>

    <!-- Submit Button -->
    <v-row justify="end" class="mt-6">
      <div class="d-flex justify-end" style="gap: 12px">
        <v-btn
          variant="text"
          data-testid="cancel-btn"
          @click="router.push(`/account/${accountName}#info`)"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          data-testid="update-btn"
          :disabled="loading || !hasChanges"
          @click="submitForm"
        >
          {{ loading ? 'Updating' : 'Update' }}
        </v-btn>
      </div>
    </v-row>

    <v-snackbar v-model="snackbar" color="info" :timeout="3000">
      {{ message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<style scoped>
.v-form {
  margin-top: 20px;
}

.v-container {
  max-width: 1200px;
  margin: 0 auto;
}

.v-expansion-panel-title {
  font-weight: bold;
}

.mt-4 {
  margin-top: 20px;
}

.height-loader {
  height: 100vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.image-div {
  height: 60px;
  width: 60px;
  /* cursor: pointer; */
  position: relative;
  border-radius: 50%;
}

.bg-logo {
  object-fit: contain;
  height: 100%;
  width: 100%;
  border-radius: 50%;
}

.plus-icon {
  z-index: 3;
  position: absolute;
  bottom: -4px;
  right: 10px;
  width: 24px;
  height: 24px;
  color: black;
  font-size: 25px;
  background-color: white;
  border-radius: 50%;
  border: 2px solid #9acbd0;

  /* border:2px solid red; */
  display: flex;
  justify-content: center;
  align-items: center;
}

.file-input-field {
  display: none;
}
</style>
